import React from 'react';
import {
  BadgeCheck,
  ClipboardCheck,
  ExternalLink,
  FileCheck2,
  IdCard,
  ShieldCheck,
  Tag
} from 'lucide-react';
import { AnimatedSection } from '../components/shared/AnimatedSection';
import { PageHero } from '../components/shared/PageHero';
import { Button } from '../components/ui/button';
import { useSEO } from '../hooks/useSEO';

const zwheelLicensePlateUrl = 'https://zwheel-shop.com/product/matricula-adhesiva-dgt-oferta-para-zwheel/';
const otherBrandsLicensePlateUrl = 'https://zwheel-shop.com/product/matricula-adhesiva-dgt-todos-los-patinetes/';
const dgtVmpRegistrationUrl = 'https://www.dgt.es/nuestros-servicios/tu-vehiculo/matricular-un-vehiculo/inscripcion-vmp/';

const regulationChanges = [
  {
    title: 'Registro en la DGT',
    description: 'Es obligatorio registrar el patinete eléctrico en el Registro de Vehículos Personales Ligeros de la DGT.',
    icon: ClipboardCheck
  },
  {
    title: 'Número único',
    description: 'Cada vehículo recibe un número de identificación único vinculado a su inscripción.',
    icon: IdCard
  },
  {
    title: 'Etiqueta visible',
    description: 'Se debe colocar una etiqueta identificativa visible en el patinete, que actúa como una matrícula.',
    icon: Tag
  },
  {
    title: 'Seguro obligatorio',
    description: 'Se debe contratar un seguro de responsabilidad civil para circular con el VMP.',
    icon: ShieldCheck
  }
];

const licensePlateOptions = [
  {
    title: 'Matrícula patinetes Zwheel/Mascooter',
    description: 'Etiqueta identificativa DGT para patinetes Zwheel y Mascooter.',
    href: zwheelLicensePlateUrl,
    tone: 'primary'
  },
  {
    title: 'Matrícula patinetes de otras marcas',
    description: 'Etiqueta identificativa DGT para VMP de otros fabricantes.',
    href: otherBrandsLicensePlateUrl,
    tone: 'secondary'
  }
];

export default function Matriculas() {
  useSEO({
    title: 'Matrículas para Patinetes Eléctricos',
    description: 'Información sobre la etiqueta identificativa DGT para patinetes eléctricos y acceso a las matrículas para Zwheel, Mascooter y otras marcas.',
    keywords: 'matrícula patinete eléctrico, etiqueta DGT VMP, matrícula Zwheel, matrícula Mascooter, registro VMP'
  });

  return (
    <div className="min-h-screen bg-white">
      <PageHero
        tag="Matrículas VMP"
        title="Matrículas para patinetes eléctricos"
        subtitle="Desde enero de 2026, la normativa en España introduce la identificación obligatoria de los VMP mediante una etiqueta visible."
      />

      <section className="bg-white py-16 md:py-24">
        <div className="container-max section-padding">
          <div className="mx-auto max-w-6xl space-y-10">
            <AnimatedSection>
              <div className="grid gap-8 lg:grid-cols-[1.05fr_0.95fr] lg:items-stretch">
                <div className="rounded-[2rem] border border-gray-200 bg-[color:var(--zw-ivory)] p-8 md:p-10">
                  <span className="mb-3 block text-sm font-bold uppercase tracking-[0.18em] text-[color:var(--zw-moss)]">
                    Nueva identificación
                  </span>
                  <h2 className="mb-5 text-3xl font-bold tracking-tight text-gray-900 md:text-4xl">
                    ¿Qué cambia exactamente a partir de 2026?
                  </h2>
                  <div className="space-y-5 text-lg leading-8 text-gray-700">
                    <p>
                      Con la nueva regulación impulsada por la DGT, es obligatorio identificar el
                      patinete eléctrico mediante una etiqueta identificativa visible, equivalente a
                      una matrícula para VMP.
                    </p>
                    <p>
                      Este sistema permite vincular cada patinete con su propietario y facilita el
                      control en caso de accidente, robo o infracción.
                    </p>
                  </div>
                </div>

                <div className="rounded-[2rem] border border-gray-200 bg-white p-8 shadow-sm md:p-10">
                  <div className="mb-6 flex items-center gap-3">
                    <FileCheck2 className="h-7 w-7 text-black" />
                    <h2 className="text-2xl font-bold text-gray-900 md:text-3xl">
                      Elige tu matrícula
                    </h2>
                  </div>
                  <div className="space-y-4">
                    {licensePlateOptions.map((option) => (
                      <a
                        key={option.href}
                        href={option.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`group block rounded-[1.5rem] border p-5 transition-all duration-200 ${
                          option.tone === 'primary'
                            ? 'border-black bg-black text-white hover:bg-gray-900'
                            : 'border-gray-200 bg-white text-gray-900 hover:border-black/20 hover:bg-[#faf7f1]'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <h3 className="text-base font-bold uppercase tracking-[0.08em]">
                              {option.title}
                            </h3>
                            <p className={`mt-3 text-sm leading-6 ${
                              option.tone === 'primary' ? 'text-white/75' : 'text-gray-600'
                            }`}>
                              {option.description}
                            </p>
                          </div>
                          <ExternalLink className="mt-1 h-5 w-5 shrink-0 transition-transform group-hover:translate-x-0.5" />
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.08}>
              <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
                {regulationChanges.map((item) => (
                  <div key={item.title} className="rounded-[1.5rem] border border-gray-200 bg-white p-6 shadow-sm">
                    <item.icon className="mb-5 h-7 w-7 text-black" />
                    <h3 className="mb-3 text-xl font-bold text-gray-900">{item.title}</h3>
                    <p className="text-sm leading-6 text-gray-600">{item.description}</p>
                  </div>
                ))}
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.14}>
              <div className="rounded-[2rem] border border-gray-200 bg-[color:var(--zw-sand)] p-8 md:p-10">
                <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
                  <div className="max-w-3xl">
                    <div className="mb-4 flex items-center gap-3">
                      <BadgeCheck className="h-7 w-7 text-black" />
                      <h2 className="text-2xl font-bold text-gray-900 md:text-3xl">
                        Registra, identifica y circula con tranquilidad
                      </h2>
                    </div>
                    <p className="text-base leading-7 text-gray-700">
                      Antes de colocar la etiqueta, revisa el trámite de inscripción del VMP y
                      conserva el certificado digital emitido por la DGT. Después, instala la
                      etiqueta en una zona visible del patinete.
                    </p>
                    <p className="mt-4 text-lg font-semibold text-gray-900">
                      ¡Y disfruta de conducir tu patinete eléctrico!
                    </p>
                  </div>

                  <Button asChild variant="outline" className="bg-white">
                    <a href={dgtVmpRegistrationUrl} target="_blank" rel="noopener noreferrer">
                      Ver trámite DGT
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </Button>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </div>
  );
}
